import { X } from "lucide-react";
import { Button } from "./ui/button";
import { useState, useEffect } from "react";

interface Asset {
  asset: string;
  [key: string]: unknown;
}

interface TradingPair {
  asset: string;
  [key: string]: unknown;
}

interface TradeRate {
  rate: number;
  [key: string]: unknown;
}

interface OrderCreationCardProps {
  isBuy: boolean;
  activeAsset?: Asset | null;
  activePair?: TradingPair | null;
  rate?: TradeRate | null;
  targetAmountHolder: number | null;
  highTarget: boolean;
  onTargetChange: (value: string) => void;
  onClose: () => void;
  onCancel: () => void;
  onDone: () => void;
}

export default function OrderCreationCard({
  isBuy,
  activeAsset,
  activePair,
  rate,
  targetAmountHolder,
  highTarget,
  onTargetChange,
  onClose,
  onCancel,
  onDone,
}: OrderCreationCardProps) {
  const [showEmptyError, setShowEmptyError] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");

  useEffect(() => {
    if (targetAmountHolder !== null) {
      setInputValue(String(targetAmountHolder));
      setShowEmptyError(false);
    } else {
      setInputValue("");
    }
  }, [targetAmountHolder]);

  const handleDoneClick = () => {
    if (!targetAmountHolder || targetAmountHolder <= 0) {
      setShowEmptyError(true);
      return;
    }
    setShowEmptyError(false);
    onDone();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setInputValue(value);

      if (value === "") {
        onTargetChange("");
        return;
      }

      if (value.endsWith(".")) {
        return;
      }

      onTargetChange(value);
    }
  };

  const isDisabled =
    highTarget || !targetAmountHolder || targetAmountHolder <= 0;

  return (
    <div className="flex justify-center items-center min-h-[580px] w-full">
      <div className="flex flex-col gap-6 max-w-md w-full border border-neutral rounded-2xl p-6 bg-white">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            Create a {isBuy ? "buy" : "sell"} order
          </h2>
          <Button
            variant="ghost"
            onClick={onClose}
            className="w-10 h-10 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-neutral hover:bg-gray-100"
          >
            <X size={18} className="text-gray-700" />
          </Button>
        </div>

        <div className="flex flex-col space-y-4">
          {rate?.rate && (
            <div className="p-5 rounded-full bg-gray-100 flex-1 flex justify-center items-center">
              <div className="text-sm text-text-primary">
                {activeAsset?.asset.toUpperCase()}/
                {activePair?.asset.toUpperCase()} ≈ {rate.rate}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-text-secondary text-sm">
              {isBuy ? "Buy" : "Sell"} when the price
              {activeAsset?.asset
                ? ` of ${activeAsset.asset.toUpperCase()}`
                : ""}{" "}
              reaches
            </label>
            <div
              className={`flex items-center gap-4 rounded-2xl p-3 border ${
                highTarget || showEmptyError ? "border border-rose-500" : ""
              }`}
            >
              <span className="text-lg font-semibold text-brand-primary">
                R
              </span>
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                className={`flex-1 px-2 !text-xl md:text-sm bg-transparent outline-none 
                  focus-visible:ring-0 focus-visible:outline-none transition-colors duration-200 ease-in-out
                  ${highTarget || showEmptyError ? "text-rose-500" : "text-purple-900"}`}
                placeholder="0.00"
              />
            </div>
            {highTarget && (
              <p className="mt-1 text-sm text-rose-500">
                {isBuy
                  ? "Target amount cannot be higher than default rate"
                  : "Target amount cannot be lower than default rate"}
              </p>
            )}
            {showEmptyError && !highTarget && (
              <p className="mt-1 text-sm text-rose-500">
                Please enter a target amount
              </p>
            )}
          </div>

          <div className="flex items-center justify-center gap-4 mt-4">
            <Button
              onClick={onCancel}
              variant="outline"
              className="rounded-full py-3 w-full border-neutral text-gray-500"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDoneClick}
              disabled={isDisabled}
              className="btn-primary py-2 h-auto w-full"
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
